import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const STAGE_LABELS = { new: "Nouveau", screened: "Trié", interview: "Entretien", offer: "Offre", hired: "Recruté", rejected: "Refusé" };
const OUTCOME_LABELS = { hired_success: "Recruté ✓", hired_failure: "Recruté ✗", rejected_good: "Refus ✓", rejected_bad: "Refus ✗" };
const COLORS = ["#2563EB", "#10B981", "#F59E0B", "#EF4444"];

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [pms, setPms] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/analytics/summary"), api.get("/post-mortems")]).then(([s, p]) => {
      setStats(s.data);
      setPms(p.data);
    });
  }, []);

  if (!stats) return <div className="p-10 text-slate-500">Chargement…</div>;

  const pipelineData = Object.entries(stats.pipeline).map(([k, v]) => ({ stage: STAGE_LABELS[k] || k, count: v }));
  const outcomeData = Object.entries(stats.post_mortem_outcomes).map(([k, v]) => ({ name: OUTCOME_LABELS[k] || k, value: v }));
  const successRate = (() => {
    const o = stats.post_mortem_outcomes;
    const total = o.hired_success + o.hired_failure;
    return total > 0 ? Math.round((o.hired_success / total) * 100) : null;
  })();

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="mb-10">
        <div className="text-xs tracking-[0.3em] uppercase text-slate-500 mb-2">Analytics & Post-mortem</div>
        <h1 className="font-display text-4xl tracking-tight">Apprentissage continu</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Analyse des recrutements et signaux pour calibrer les règles de scoring.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 mb-10">
        <div className="bg-white p-6">
          <div className="text-xs uppercase tracking-wider text-slate-500">Taux de succès</div>
          <div className="font-mono text-5xl font-bold mt-3 tabular-nums">{successRate ?? "—"}{successRate != null ? <span className="text-2xl text-slate-400">%</span> : ""}</div>
          <div className="text-xs text-slate-500 mt-2">Sur les recrutements clôturés</div>
        </div>
        <div className="bg-white p-6">
          <div className="text-xs uppercase tracking-wider text-slate-500">Post-mortems</div>
          <div className="font-mono text-5xl font-bold mt-3 tabular-nums">{pms.length}</div>
          <div className="text-xs text-slate-500 mt-2">Apprentissages capitalisés</div>
        </div>
        <div className="bg-white p-6">
          <div className="text-xs uppercase tracking-wider text-slate-500">Actions ouvertes</div>
          <div className="font-mono text-5xl font-bold mt-3 tabular-nums">{stats.open_actions}</div>
          <div className="text-xs text-slate-500 mt-2">À traiter cette semaine</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-10">
        <div className="border border-slate-200 bg-white p-6">
          <h2 className="font-display text-xl mb-4">Pipeline</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pipelineData}>
              <XAxis dataKey="stage" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip />
              <Bar dataKey="count" fill="#2563EB" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-slate-200 bg-white p-6">
          <h2 className="font-display text-xl mb-4">Issues des recrutements</h2>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={outcomeData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90}>
                {outcomeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl mb-4">Apprentissages récents</h2>
        <div className="border border-slate-200 bg-white">
          {pms.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              Aucun post-mortem enregistré. Lorsqu'un recrutement est clos, créez un post-mortem depuis la fiche candidat pour enrichir l'apprentissage.
            </div>
          ) : (
            pms.slice(0, 10).map((pm) => (
              <div key={pm.id} className="p-5 border-b last:border-b-0 border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">{pm.candidate_name}</div>
                  <div className="text-xs font-mono text-slate-500">{OUTCOME_LABELS[pm.outcome]}{pm.months_in_role ? ` · ${pm.months_in_role} mois` : ""}</div>
                </div>
                <p className="text-sm text-slate-700">{pm.notes}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
